import { useEffect, useRef, useState } from "react"
import { ShareMessageFragment } from "~/__generated__/graphql"
import { FeedMessage } from "./message/feed-message"
import { AppText } from "~/ui/app-text"
import { cn } from "~/common/cn"

const JoinOverlay = ({
  visible,
  onCtaClick,
  topOffset,
  variant = "mobile",
}: {
  visible: boolean
  onCtaClick?: () => void
  topOffset?: number
  variant?: "mobile" | "desktop"
}) => (
  <div
    className={cn(
      "pointer-events-none absolute inset-x-0 z-20 flex items-start justify-center bg-gradient-to-b from-base-white from-10% via-base-white/80 via-50% to-transparent transition-all duration-500",
      variant === "desktop" ? "pt-14" : "pt-4",
      visible ? "h-44 opacity-100" : "h-0 opacity-0"
    )}
    style={{ top: topOffset ?? 0 }}
  >
    <button
      className={cn(
        "pointer-events-auto rounded-full bg-base-blue px-4 py-1.5 shadow-md transition-transform active:scale-95",
        variant === "desktop" && "pointer-events-none cursor-default"
      )}
      onClick={onCtaClick}
    >
      <AppText variant="body3-medium" className="text-base-white">
        Join to see the full chat
      </AppText>
    </button>
  </div>
)

interface MessageFeedProps {
  messages: ShareMessageFragment[]
  onNearTop?: (nearTop: boolean) => void
  onAtBottom?: () => void
  onCtaClick?: () => void
  variant?: "mobile" | "desktop"
  contentPaddingTop?: number
  headerContent?: React.ReactNode
}

export const MessageFeed = ({
  messages,
  onNearTop,
  onAtBottom,
  onCtaClick,
  variant = "mobile",
  contentPaddingTop,
  headerContent,
}: MessageFeedProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const headerSentinelRef = useRef<HTMLDivElement>(null)
  const hasHeader = !!headerContent
  const [showOverlay, setShowOverlay] = useState(!hasHeader)
  const onNearTopRef = useRef(onNearTop)
  const onAtBottomRef = useRef(onAtBottom)
  useEffect(() => {
    onNearTopRef.current = onNearTop
    onAtBottomRef.current = onAtBottom
  })

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let collapseTimer: ReturnType<typeof setTimeout> | null = null
    let lastExpanded = true

    const getState = () => {
      const maxScroll = el.scrollHeight - el.clientHeight
      const spacer = contentPaddingTop ?? 0

      const atTop = el.scrollTop < spacer + 40

      let inOverlayZone = false
      if (hasHeader && headerSentinelRef.current) {
        const sentinelRect = headerSentinelRef.current.getBoundingClientRect()
        const scrollRect = el.getBoundingClientRect()
        const distPastHeader = scrollRect.top + spacer + 20 - sentinelRect.bottom
        inOverlayZone = distPastHeader > 0 && distPastHeader < 200
      }

      const nearTop = hasHeader ? inOverlayZone : atTop
      const nearBottom = el.scrollTop >= maxScroll - 60
      return { nearTop, nearBottom, shouldExpand: atTop || nearTop || nearBottom }
    }

    const handleScroll = () => {
      const { nearTop, nearBottom, shouldExpand } = getState()
      setShowOverlay(nearTop)

      if (variant !== "mobile") return

      if (shouldExpand && !lastExpanded) {
        if (collapseTimer) {
          clearTimeout(collapseTimer)
          collapseTimer = null
        }
        lastExpanded = true
        onNearTopRef.current?.(true)
        if (nearBottom) onAtBottomRef.current?.()
      } else if (!shouldExpand && lastExpanded) {
        if (!collapseTimer) {
          collapseTimer = setTimeout(() => {
            collapseTimer = null
            const rechecked = getState()
            if (!rechecked.shouldExpand) {
              lastExpanded = false
              onNearTopRef.current?.(false)
            }
          }, 500)
        }
      } else if (shouldExpand && lastExpanded) {
        if (collapseTimer) {
          clearTimeout(collapseTimer)
          collapseTimer = null
        }
        if (nearBottom) onAtBottomRef.current?.()
      }
    }

    const timerId = setTimeout(() => {
      el.addEventListener("scroll", handleScroll, { passive: true })
    }, 300)

    return () => {
      clearTimeout(timerId)
      if (collapseTimer) clearTimeout(collapseTimer)
      el.removeEventListener("scroll", handleScroll)
    }
  }, [variant, contentPaddingTop, hasHeader])

  // Messages come in created_at_desc (newest first). Reverse to chronological order (oldest first).
  const chronological = [...messages].reverse()
  const messageList = chronological.map((message, index) => {
    const prev = index > 0 ? chronological[index - 1] : null
    const next = index < chronological.length - 1 ? chronological[index + 1] : null
    const isSameUserAsPrev = prev?.user?.id === message.user?.id
    const isSameUserAsNext = next?.user?.id === message.user?.id
    const prevHasReactions =
      (prev?.reactionGroups?.length ?? 0) > 0 || (prev?.childMessagesCount ?? 0) > 0

    return (
      <div
        key={message.id}
        className={
          isSameUserAsPrev ? (prevHasReactions ? "mt-1" : "mt-0.5") : index === 0 ? "" : "mt-2"
        }
      >
        <FeedMessage
          message={message}
          showName={!isSameUserAsPrev}
          showAvatar={!isSameUserAsNext}
          isFirstInGroup={!isSameUserAsPrev}
          isLastInGroup={!isSameUserAsNext}
        />
      </div>
    )
  })

  return (
    <div
      className={cn(
        "relative flex w-full flex-col",
        variant === "desktop" ? "h-full" : "flex-1 overflow-hidden"
      )}
    >
      <JoinOverlay
        visible={showOverlay}
        onCtaClick={onCtaClick}
        topOffset={contentPaddingTop}
        variant={variant}
      />
      <div
        ref={scrollRef}
        className={cn(
          "flex flex-col overflow-y-auto [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          hasHeader ? "overscroll-none" : "overscroll-contain",
          variant === "desktop" ? "h-full bg-base-white px-1 pb-4 pt-14" : "flex-1"
        )}
      >
        {contentPaddingTop ? (
          <div className="shrink-0" style={{ height: contentPaddingTop }} />
        ) : null}
        {headerContent && (
          <div className="shrink-0 bg-base-blue px-3 pb-4">
            {headerContent}
            <div ref={headerSentinelRef} />
          </div>
        )}
        <div className={variant === "desktop" ? "" : "bg-base-white pb-2 pl-2 pr-4 pt-4"}>
          {messageList}
        </div>
      </div>
    </div>
  )
}
